import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Badge, Button, Card, Col, Container, Fade, Form, Modal, Row, Spinner} from 'react-bootstrap';
import {Helmet} from 'react-helmet';
import {useHistory, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import * as api from '../api';
import {refreshPartnerProfile} from '../actions/partner';
import {refreshPlayerProfile} from '../actions/player';
import Configuration from '../config';
import {ROLES} from '../constants';
import {askInvoiceRequest, hasCompleteInvoiceData} from '../utils/invoice-request';
import {FaCartPlus, FaShoppingBag, FaTrashAlt, FaWallet} from 'react-icons/fa';
import {clearBookingTopupResume} from '../util/booking-topup-resume';
import {clearBookingInvitationResume} from '../util/booking-invitation-resume';
import {
  saveShopTopupResume,
  shopReturnPath,
  suggestedShopTopupAmount
} from '../util/shop-topup-resume';

const formatEur = (value, locale = 'it-IT') => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return '-';
  }

  return (numeric / 100).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const getVariantLabel = variant => {
  const parts = [variant.size, variant.color].filter(Boolean);
  return parts.length > 0 ? parts.join(' - ') : variant.sku;
};

const getImageSrc = (product, image) => {
  if (!image) {
    return product.image_url ? `${Configuration.apiEndPoint}/shop/products/${product.id}/image` : null;
  }

  return `${Configuration.apiEndPoint}/shop/products/${product.id}/images/${image.id}`;
};

const getProductImages = (product, selectedVariant) => {
  const images = product.images || [];
  if (!selectedVariant) {
    const genericImages = images.filter(image => image.variant_id == null);
    return genericImages.length > 0 ? genericImages : images;
  }

  const variantImages = images.filter(image => image.variant_id === selectedVariant.id);
  if (variantImages.length > 0) {
    return variantImages;
  }

  const colorImages = selectedVariant.color
    ? images.filter(image => image.variant_color === selectedVariant.color)
    : [];
  if (colorImages.length > 0) {
    return colorImages;
  }

  const genericImages = images.filter(image => image.variant_id == null);
  return genericImages.length > 0 ? genericImages : images;
};

const getProductPrice = (product, variant) => {
  if (variant && variant.price_cents != null) {
    return variant.price_cents;
  }

  if (product.sale_price_cents != null && product.sale_price_cents < product.price_cents) {
    return product.sale_price_cents;
  }

  return product.price_cents;
};

const getSelectedVariant = (product, selectedVariantId) => {
  if (!product || !product.variants || product.variants.length === 0) {
    return null;
  }

  const numericId = Number(selectedVariantId);
  const explicitMatch = Number.isNaN(numericId)
    ? null
    : product.variants.find(item => item.id === numericId);

  if (explicitMatch) {
    return explicitMatch;
  }

  return product.variants.find(item => item.active && item.stock_quantity > 0) || product.variants[0];
};

const CART_STORAGE_KEY = 'replayer-shop-cart';
const DEFAULT_SHOP_SETTINGS = {
  shipping_enabled: true,
  shipping_cost_cents: 0,
  free_shipping_threshold_cents: null,
  pickup_enabled: false
};

const isBlank = value => value == null || String(value).trim() === '';

const getDeliveryProfile = profile => {
  if (!profile || !profile.user) {
    return null;
  }

  return {
    name: [profile.name, profile.surname].filter(Boolean).join(' ') ||
      profile.businessName ||
      profile.name ||
      profile.user.email,
    email: profile.user.email,
    phone: profile.phoneNumber,
    address: profile.address,
    cap: profile.cap || profile.CAP,
    city: profile.city,
    province: profile.province,
    country: profile.country || 'Italia'
  };
};

const getMissingDeliveryFields = (deliveryProfile, t) => {
  if (!deliveryProfile) {
    return [t('PUBLIC_SHOP_DELIVERY_PROFILE')];
  }

  return [
    ['name', t('NAME')],
    ['phone', t('PHONE')],
    ['address', t('ADDRESS')],
    ['cap', t('CAP')],
    ['city', t('CITY')],
    ['province', t('PROVINCE')]
  ].filter(([key]) => isBlank(deliveryProfile[key])).map(([, label]) => label);
};

const normalizeDeliveryData = delivery => ({
  name: delivery && delivery.name ? delivery.name : '',
  email: delivery && delivery.email ? delivery.email : '',
  phone: delivery && delivery.phone ? delivery.phone : '',
  address: delivery && delivery.address ? delivery.address : '',
  cap: delivery && delivery.cap ? delivery.cap : '',
  city: delivery && delivery.city ? delivery.city : '',
  province: delivery && delivery.province ? delivery.province : '',
  country: delivery && delivery.country ? delivery.country : 'Italia',
  notes: delivery && delivery.notes ? delivery.notes : ''
});

const loadCart = storageKey => {
  try {
    const cart = JSON.parse(window.localStorage.getItem(storageKey));
    return Array.isArray(cart) ? cart : [];
  } catch (error) {
    return [];
  }
};

const PublicShop = ({match}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const {t, i18n} = useTranslation();
  const partnerSlug = match && match.params ? match.params.partnerSlug : null;
  const cartStorageKey = partnerSlug ? `${CART_STORAGE_KEY}-${partnerSlug}` : CART_STORAGE_KEY;
  const shopTitle = partnerSlug ? t('PUBLIC_SHOP_TITLE_PARTNER') : t('PUBLIC_SHOP_TITLE_REPLAYER');
  const token = useSelector(state => state.login.token);
  const profile = useSelector(state => state.login.profile);
  const [products, setProducts] = useState([]);
  const [shopSettings, setShopSettings] = useState(DEFAULT_SHOP_SETTINGS);
  const [shopPartner, setShopPartner] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState('shipping');
  const [selectedVariants, setSelectedVariants] = useState({});
  const [quantities, setQuantities] = useState({});
  const [cart, setCart] = useState(() => loadCart(cartStorageKey));
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [useAccountDelivery, setUseAccountDelivery] = useState(true);
  const [deliveryForm, setDeliveryForm] = useState(normalizeDeliveryData(null));
  const [deliveryConfirmed, setDeliveryConfirmed] = useState(false);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [failedImages, setFailedImages] = useState({});
  const [selectedProductImages, setSelectedProductImages] = useState({});
  const [showDeliveryConfirmModal, setShowDeliveryConfirmModal] = useState(false);
  const [topupPrompt, setTopupPrompt] = useState(null);
  const [invoiceRequested, setInvoiceRequested] = useState(false);
  const cartRef = useRef(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      const [res, settingsRes, partnerRes] = await Promise.all([
        api.getPublicShopProducts({partnerSlug}),
        api.getPublicShopSettings({partnerSlug}),
        partnerSlug
          ? api.getPublicPartnerCatalog(partnerSlug, {limit: 1})
          : Promise.resolve({status: 200, json: {partner: null}})
      ]);
      if (!active) {
        return;
      }
      if (res.status === 200) {
        const nextProducts = Array.isArray(res.json) ? res.json : [];
        setProducts(nextProducts);
        setSelectedVariants(
          nextProducts.reduce((acc, product) => {
            const firstVariant = product.variants && product.variants.length > 0
              ? product.variants.find(item => item.active && item.stock_quantity > 0) || product.variants[0]
              : null;
            if (firstVariant) {
              acc[product.id] = String(firstVariant.id);
            }
            return acc;
          }, {})
        );
      } else {
        setError(res.json.error || t('PUBLIC_SHOP_LOAD_ERROR'));
      }
      if (settingsRes.status === 200) {
        const nextSettings = {...DEFAULT_SHOP_SETTINGS, ...settingsRes.json};
        setShopSettings(nextSettings);
        setDeliveryMethod(nextSettings.shipping_enabled ? 'shipping' : 'pickup');
      }
      if (partnerSlug && partnerRes.status === 200 && partnerRes.json.partner) {
        setShopPartner(partnerRes.json.partner);
      }
      setLoading(false);
    };

    load();

    return () => {
      active = false;
    };
  }, [partnerSlug, t]);

  useEffect(() => {
    setCart(loadCart(cartStorageKey));
  }, [cartStorageKey]);

  useEffect(() => {
    window.localStorage.setItem(cartStorageKey, JSON.stringify(cart));
  }, [cart, cartStorageKey]);

  useEffect(() => {
    setDeliveryConfirmed(false);
  }, [cart, profile]);

  useEffect(() => {
    setDeliveryForm(normalizeDeliveryData(getDeliveryProfile(profile)));
  }, [profile]);

  useEffect(() => {
    setDeliveryConfirmed(false);
  }, [deliveryForm, useAccountDelivery]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('shopResume') !== '1' || loading || !cartRef.current) return;
    cartRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
    setNotice({variant: 'success', message: t('PUBLIC_SHOP_TOPUP_READY')});
    history.replace(partnerSlug ? `/shop/${partnerSlug}#shop-cart` : '/shop#shop-cart');
  }, [history, loading, location.search, partnerSlug, t]);

  const publishedCount = useMemo(() => products.length, [products]);
  const partnerLogoUrl = partnerSlug && shopPartner && shopPartner.logoUrl
    ? `${Configuration.apiEndPoint}/images/${shopPartner.id}?load=${encodeURIComponent(shopPartner.logoUrl)}`
    : null;
  const displayShopTitle = partnerSlug && shopPartner
    ? shopPartner.name
    : 'Circolo sportivo';
  const wallet = profile && profile.user ? profile.user.wallet : null;
  const canUseWallet = profile && profile.user &&
    (profile.user.role === ROLES.player || profile.user.role === ROLES.partner);
  const cartItems = useMemo(() => cart.map(item => {
    const product = products.find(candidate => candidate.id === item.productId);
    const variant = product && product.variants
      ? product.variants.find(candidate => candidate.id === item.variantId)
      : null;
    return product && variant ? {
      ...item,
      product,
      variant,
      unitPriceCents: getProductPrice(product, variant)
    } : null;
  }).filter(Boolean), [cart, products]);
  const cartQuantity = cartItems.reduce((total, item) => total + item.quantity, 0);
  const cartTotalCents = cartItems.reduce(
    (total, item) => total + (item.unitPriceCents * item.quantity),
    0
  );
  const shippingCents = deliveryMethod === 'shipping' && shopSettings.shipping_enabled &&
    !(shopSettings.free_shipping_threshold_cents != null &&
      cartTotalCents >= shopSettings.free_shipping_threshold_cents)
    ? Number(shopSettings.shipping_cost_cents || 0)
    : 0;
  const orderTotalCents = cartTotalCents + shippingCents;
  const deliveryProfile = useMemo(() => getDeliveryProfile(profile), [profile]);
  const selectedDelivery = useMemo(
    () => normalizeDeliveryData(useAccountDelivery ? deliveryProfile : deliveryForm),
    [deliveryForm, deliveryProfile, useAccountDelivery]
  );
  const missingDeliveryFields = useMemo(
    () => getMissingDeliveryFields(selectedDelivery, t),
    [selectedDelivery, t]
  );
  const deliveryProfilePath = profile && profile.user && profile.user.role === ROLES.partner
    ? '/partner-profile'
    : '/player-profile';
  const canCheckoutWithDelivery = deliveryMethod === 'pickup' || missingDeliveryFields.length === 0;

  const updateDeliveryField = (field, value) => {
    setDeliveryForm({...deliveryForm, [field]: value});
  };

  const buildDeliveryPayload = () => ({
    same_as_account: useAccountDelivery,
    name: selectedDelivery.name,
    phone: selectedDelivery.phone,
    address: selectedDelivery.address,
    cap: selectedDelivery.cap,
    city: selectedDelivery.city,
    province: selectedDelivery.province,
    country: selectedDelivery.country || 'Italia',
    notes: selectedDelivery.notes
  });

  const getQuantity = productId => quantities[productId] || 1;

  const updateQuantity = (productId, value, max) => {
    const parsed = Number(value);
    const nextQuantity = Number.isInteger(parsed) ? Math.max(1, Math.min(parsed, max || 1)) : 1;
    setQuantities({...quantities, [productId]: nextQuantity});
  };

  const refreshWallet = () => {
    if (!profile || !profile.user) return;
    if (profile.user.role === ROLES.player) {
      dispatch(refreshPlayerProfile());
    } else if (profile.user.role === ROLES.partner) {
      dispatch(refreshPartnerProfile());
    }
  };

  const addToCart = (product, variant) => {
    if (!variant) {
      setNotice({variant: 'danger', message: t('PUBLIC_SHOP_SELECT_VARIANT')});
      return;
    }
    const quantity = getQuantity(product.id);
    setCart(currentCart => {
      const existing = currentCart.find(item => item.variantId === variant.id);
      if (!existing) {
        return [...currentCart, {productId: product.id, variantId: variant.id, quantity}];
      }
      return currentCart.map(item => item.variantId === variant.id
        ? {...item, quantity: Math.min(item.quantity + quantity, variant.stock_quantity)}
        : item);
    });
    setQuantities({...quantities, [product.id]: 1});
    setNotice({variant: 'success', message: t('PUBLIC_SHOP_ADDED_TO_CART', {product: product.name})});
  };

  const updateCartQuantity = (variantId, value, max) => {
    const parsed = Number(value);
    const quantity = Number.isInteger(parsed) ? Math.max(1, Math.min(parsed, max || 1)) : 1;
    setCart(cart.map(item => item.variantId === variantId ? {...item, quantity} : item));
  };

  const removeFromCart = variantId => {
    setCart(cart.filter(item => item.variantId !== variantId));
  };

  const checkout = async ({skipDeliveryConfirmation = false, skipPurchaseConfirmation = false} = {}) => {
    if (!token || !profile) {
      window.location.href = `/login?next=${encodeURIComponent(partnerSlug ? `/shop/${partnerSlug}` : '/shop')}`;
      return;
    }
    if (!canUseWallet) {
      setNotice({variant: 'danger', message: t('PUBLIC_SHOP_PROFILE_CANNOT_PURCHASE')});
      return;
    }
    if (cartItems.length === 0) {
      setNotice({variant: 'danger', message: t('PUBLIC_SHOP_CART_EMPTY')});
      return;
    }
    if (deliveryMethod === 'shipping' && missingDeliveryFields.length > 0) {
      setNotice({
        variant: 'danger',
        message: t('PUBLIC_SHOP_COMPLETE_DELIVERY', {fields: missingDeliveryFields.join(', ')})
      });
      return;
    }
    if (deliveryMethod === 'shipping' && !deliveryConfirmed && !skipDeliveryConfirmation) {
      setShowDeliveryConfirmModal(true);
      return;
    }
    if (!skipPurchaseConfirmation && !window.confirm(t('PUBLIC_SHOP_CONFIRM_PURCHASE', {quantity: cartQuantity, total: formatEur(orderTotalCents)}))) {
      return;
    }
    const invoiceChoice = askInvoiceRequest(profile, invoiceRequested);
    if (invoiceChoice.blocked) {
      setNotice({
        variant: 'warning',
        message: t('PUBLIC_SHOP_INVOICE_DATA_REQUIRED')
      });
      return;
    }

    setNotice(null);
    setCheckoutLoading(true);
    const messages = {
      not_enough_credit: t('PUBLIC_SHOP_NOT_ENOUGH_CREDIT'),
      not_enough_stock: t('PUBLIC_SHOP_NOT_ENOUGH_STOCK'),
      product_not_available: t('PUBLIC_SHOP_PRODUCT_NOT_AVAILABLE'),
      variant_not_found: t('PUBLIC_SHOP_VARIANT_NOT_AVAILABLE'),
      shipping_not_available: t('PUBLIC_SHOP_SHIPPING_NOT_AVAILABLE'),
      pickup_not_available: t('PUBLIC_SHOP_PICKUP_NOT_AVAILABLE'),
      missing_delivery: t('PUBLIC_SHOP_MISSING_DELIVERY'),
      invalid_delivery: t('PUBLIC_SHOP_INVALID_DELIVERY')
    };
    const response = await api.purchaseShopOrder(
      token,
      cartItems.map(item => ({
        variant_id: item.variant.id,
        quantity: item.quantity
      })),
      deliveryMethod === 'shipping' ? buildDeliveryPayload() : {},
      {partnerSlug, deliveryMethod, invoiceRequested: invoiceChoice.invoiceRequested}
    );
    if (response.status !== 201) {
      setCheckoutLoading(false);
      refreshWallet();
      if (response.json && response.json.error === 'not_enough_credit') {
        const walletCents = Number(response.json.wallet_cents != null
          ? response.json.wallet_cents
          : Number(wallet || 0) * 100);
        const totalCents = Number(response.json.total_cents != null
          ? response.json.total_cents
          : orderTotalCents);
        setTopupPrompt({
          walletCents,
          totalCents,
          shortfallCents: Math.max(0, totalCents - walletCents),
          amount: suggestedShopTopupAmount(totalCents, walletCents)
        });
        return;
      }
      setNotice({
        variant: 'danger',
        message: messages[response.json.error] || t('PUBLIC_SHOP_PURCHASE_FAILED')
      });
      return;
    }

    const updatedProducts = response.json.products || [];
    setProducts(products.map(product =>
      updatedProducts.find(updated => updated.id === product.id) || product));
    setCart([]);
    setCheckoutLoading(false);
    setNotice({
      variant: 'success',
      message: t('PUBLIC_SHOP_ORDER_COMPLETED', {order: response.json.order.number, balance: formatEur(response.json.wallet_cents)})
    });
    refreshWallet();
  };

  const confirmDeliveryAndCheckout = () => {
    setDeliveryConfirmed(true);
    setShowDeliveryConfirmModal(false);
    checkout({skipDeliveryConfirmation: true, skipPurchaseConfirmation: true});
  };

  const openShopTopup = () => {
    if (!topupPrompt) return;
    const returnPath = shopReturnPath(partnerSlug);
    const saved = saveShopTopupResume({
      returnPath,
      amount: topupPrompt.amount,
      cartStorageKey
    });
    if (!saved) {
      setTopupPrompt(null);
      setNotice({variant: 'danger', message: t('PUBLIC_SHOP_TOPUP_RESUME_ERROR')});
      return;
    }
    clearBookingTopupResume();
    clearBookingInvitationResume();
    setTopupPrompt(null);
    history.push(`/user-top-up-account?amount=${encodeURIComponent(topupPrompt.amount)}&shopResume=1`);
  };

  return (
    <Fragment>
      <Helmet>
        <title>{shopTitle}</title>
      </Helmet>
      <Modal
        show={showDeliveryConfirmModal}
        onHide={() => setShowDeliveryConfirmModal(false)}
        centered
        className="public-shop-delivery-confirm-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('PUBLIC_SHOP_CONFIRM_DELIVERY_TITLE')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            {t('PUBLIC_SHOP_CONFIRM_DELIVERY_BODY')}
          </p>
          <div className="public-shop-delivery-confirm-modal__summary">
            <span>{t('PUBLIC_SHOP_TOTAL_ORDER')}</span>
            <strong>EUR {formatEur(orderTotalCents, i18n.language || 'it-IT')}</strong>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeliveryConfirmModal(false)}>
            {t('PUBLIC_SHOP_CHECK_DATA')}
          </Button>
          <Button className="public-shop-action public-shop-action--primary" onClick={confirmDeliveryAndCheckout} disabled={checkoutLoading}>
            {t('PUBLIC_SHOP_CONFIRM_AND_PAY')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={topupPrompt !== null}
        onHide={() => setTopupPrompt(null)}
        centered
        className="public-shop-delivery-confirm-modal public-shop-topup-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title><FaWallet aria-hidden="true"/> {t('PUBLIC_SHOP_TOPUP_TITLE')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('PUBLIC_SHOP_TOPUP_BODY')}</p>
          {topupPrompt ? (
            <div className="public-shop-topup-modal__amounts">
              <div><span>{t('PUBLIC_SHOP_AVAILABLE_CREDIT')}</span><strong>EUR {formatEur(topupPrompt.walletCents)}</strong></div>
              <div><span>{t('PUBLIC_SHOP_TOTAL_ORDER')}</span><strong>EUR {formatEur(topupPrompt.totalCents)}</strong></div>
              <div className="is-missing"><span>{t('PUBLIC_SHOP_CREDIT_MISSING')}</span><strong>EUR {formatEur(topupPrompt.shortfallCents)}</strong></div>
            </div>
          ) : null}
          <p className="public-shop-topup-modal__return">{t('PUBLIC_SHOP_TOPUP_RETURN')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button className="public-shop-action public-shop-action--secondary" onClick={() => setTopupPrompt(null)}>{t('CANCEL')}</Button>
          <Button className="public-shop-action public-shop-action--primary" onClick={openShopTopup}>
            <FaWallet aria-hidden="true"/> {t('PUBLIC_SHOP_TOPUP_ACTION')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Fade in appear className="bg_white" style={{transition: 'opacity 0.30s linear'}}>
        <Container fluid className="presentation-container">
          <div className="page-header header-filter clear-filter dashboard_hero_rp" data-parallax="true">
            <div className="dashboard_hero_overlay_rp"/>
          </div>
          <div className="main main_raised_rp">
            <div className="section public-shop-skywork" style={{paddingBottom: 160}}>
              <div className="container">
                <Row className="public-shop-skywork__hero">
                  <Col md={12}>
                    <div className="public-shop-skywork__hero-card">
                      <div className={`public-shop-skywork__hero-icon ${partnerSlug ? 'is-partner' : ''}`} aria-hidden={partnerLogoUrl ? undefined : true}>
                      {partnerLogoUrl
                          ? <img src={partnerLogoUrl} alt={`Logo ${shopPartner.name}`}/>
                          : <FaShoppingBag/>}
                      </div>
                      <div className={`public-shop-skywork__hero-copy ${partnerSlug ? 'is-partner' : ''}`}>
                        {partnerSlug && <span className="public-shop-skywork__kicker">{t('PUBLIC_SHOP_OFFICIAL_CLUB')}</span>}
                        <h2 className={`section_title_neg_rp ${partnerSlug ? '' : 'is-replayer-shop'}`}>{partnerSlug ? displayShopTitle : t('PUBLIC_SHOP_TITLE_REPLAYER_HERO')}</h2>
                        <p>{partnerSlug
                          ? t('PUBLIC_SHOP_HERO_DESC_PARTNER', {partner: shopPartner ? shopPartner.name : t('PUBLIC_SHOP_HERO_DESC_PARTNER_FALLBACK')})
                          : t('PUBLIC_SHOP_HERO_DESC_REPLAYER')}</p>
                      </div>
                      {canUseWallet && (
                        <div className="public-shop-skywork__wallet"><FaWallet aria-hidden="true"/><span>{t('PUBLIC_SHOP_AVAILABLE_CREDIT')}</span><strong>EUR {formatEur(wallet * 100, i18n.language || 'it-IT')}</strong></div>
                      )}
                    </div>
                  </Col>
                </Row>

                {notice && (
                  <Row style={{marginTop: 20}}>
                    <Col md={12}>
                      <Alert variant={notice.variant}>{notice.message}</Alert>
                    </Col>
                  </Row>
                )}

                {!loading && !error && (
                  <Row id="shop-cart" ref={cartRef} className="public-shop-skywork__cart-row">
                    <Col md={12}>
                      <Card className={`public-shop-cart ${cartItems.length > 0 ? 'has-items' : ''}`}>
                        <Card.Body>
                          <div className="public-shop-cart__head">
                            <div><span className="public-shop-skywork__kicker">{t('PUBLIC_SHOP_YOUR_ORDER')}</span><Card.Title className="mb-0"><FaCartPlus aria-hidden="true"/> {t('PUBLIC_SHOP_CART')} <span>{cartQuantity}</span></Card.Title></div>
                            {cartItems.length > 0 && (
                              <Button className="public-shop-action public-shop-action--danger" variant="outline-danger" size="sm" onClick={() => setCart([])}>
                                <FaTrashAlt aria-hidden="true"/> {t('PUBLIC_SHOP_CLEAR_CART')}
                              </Button>
                            )}
                          </div>
                          {cartItems.length === 0 ? (
                            <div className="public-shop-cart__empty"><FaShoppingBag aria-hidden="true"/><div><strong>{t('PUBLIC_SHOP_CART_EMPTY_TITLE')}</strong><span>{t('PUBLIC_SHOP_CART_EMPTY_DESC')}</span></div></div>
                          ) : (
                            <Fragment>
                              {cartItems.map(item => (
                                <Row key={item.variant.id} className="public-shop-cart__item align-items-center">
                                  <Col md={5}>
                                    <strong>{item.product.name}</strong>
                                    <div className="small text-muted">{getVariantLabel(item.variant)}</div>
                                  </Col>
                                  <Col md={2}>EUR {formatEur(item.unitPriceCents, i18n.language || 'it-IT')}</Col>
                                  <Col md={2}>
                                    <Form.Control
                                      type="number"
                                      min="1"
                                      max={Math.max(item.variant.stock_quantity, 1)}
                                      value={item.quantity}
                                      onChange={event => updateCartQuantity(
                                        item.variant.id,
                                        event.target.value,
                                        item.variant.stock_quantity
                                      )}
                                    />
                                  </Col>
                                  <Col md={2}>
                                    <strong>EUR {formatEur(item.unitPriceCents * item.quantity, i18n.language || 'it-IT')}</strong>
                                  </Col>
                                  <Col md={1}>
                                    <Button className="public-shop-cart__remove" variant="outline-danger" size="sm" onClick={() => removeFromCart(item.variant.id)} aria-label={t('REMOVE_ITEM_NAME', {name: item.product.name})}>
                                      <FaTrashAlt aria-hidden="true"/>
                                    </Button>
                                  </Col>
                                </Row>
                              ))}
                              {(shopSettings.shipping_enabled || shopSettings.pickup_enabled) && (
                                <Card className="public-shop-checkout-panel mb-3">
                                  <Card.Body>
                                    <h5 className="mb-3">{t('PUBLIC_SHOP_RECEIVE_ORDER')}</h5>
                                    {shopSettings.shipping_enabled && (
                                      <Form.Check
                                        type="radio"
                                        id="shop-method-shipping"
                                        name="shop-delivery-method"
                                        className="mb-2"
                                        checked={deliveryMethod === 'shipping'}
                                        onChange={() => setDeliveryMethod('shipping')}
                                        label={t('PUBLIC_SHOP_SHIPPING_LABEL', {shipping: shippingCents > 0 ? `EUR ${formatEur(shippingCents)}` : t('PUBLIC_SHOP_FREE')})}
                                      />
                                    )}
                                    {shopSettings.pickup_enabled && (
                                      <Form.Check
                                        type="radio"
                                        id="shop-method-pickup"
                                        name="shop-delivery-method"
                                        checked={deliveryMethod === 'pickup'}
                                        onChange={() => setDeliveryMethod('pickup')}
                                        label={t('PUBLIC_SHOP_PICKUP_LABEL')}
                                      />
                                    )}
                                  </Card.Body>
                                </Card>
                              )}
                              {deliveryMethod === 'shipping' && <Card className="public-shop-checkout-panel mb-3">
                                <Card.Body>
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <h5 className="mb-2">{t('PUBLIC_SHOP_CHECK_DELIVERY_TITLE')}</h5>
                                      <p className="text-muted mb-3">
                                        {t('PUBLIC_SHOP_CHECK_DELIVERY_DESC')}
                                      </p>
                                    </div>
                                    {useAccountDelivery && (
                                      <Button
                                        className="public-shop-delivery-edit"
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => history.push(deliveryProfilePath)}
                                      >
                                        {t('PUBLIC_SHOP_EDIT_ACCOUNT_DATA')}
                                      </Button>
                                    )}
                                  </div>
                                  <Form.Group className="mb-3">
                                    <Form.Check
                                      inline
                                      type="radio"
                                      id="shop-delivery-account"
                                      name="shop-delivery-mode"
                                      label={t('PUBLIC_SHOP_USE_ACCOUNT_ADDRESS')}
                                      checked={useAccountDelivery}
                                      onChange={() => {
                                        setUseAccountDelivery(true);
                                        setDeliveryConfirmed(false);
                                      }}
                                    />
                                    <Form.Check
                                      inline
                                      type="radio"
                                      id="shop-delivery-custom"
                                      name="shop-delivery-mode"
                                      label={t('PUBLIC_SHOP_USE_DIFFERENT_ADDRESS')}
                                      checked={!useAccountDelivery}
                                      onChange={() => {
                                        setUseAccountDelivery(false);
                                        setDeliveryConfirmed(false);
                                      }}
                                    />
                                  </Form.Group>
                                  {missingDeliveryFields.length > 0 && (
                                    <Alert variant="warning">
                                      {t('PUBLIC_SHOP_MISSING_FIELDS', {fields: missingDeliveryFields.join(', ')})}
                                    </Alert>
                                  )}
                                  {useAccountDelivery && deliveryProfile && (
                                    <Row>
                                      <Col md={6}>
                                        <p className="mb-1"><strong>{t('NAME')}:</strong> {deliveryProfile.name || '-'}</p>
                                        <p className="mb-1"><strong>{t('EMAIL')}:</strong> {deliveryProfile.email || '-'}</p>
                                        <p className="mb-1"><strong>{t('PHONE')}:</strong> {deliveryProfile.phone || '-'}</p>
                                      </Col>
                                      <Col md={6}>
                                        <p className="mb-1"><strong>{t('ADDRESS')}:</strong> {deliveryProfile.address || '-'}</p>
                                        <p className="mb-1">
                                          <strong>{t('PUBLIC_SHOP_LOCATION')}:</strong>{' '}
                                          {[deliveryProfile.cap, deliveryProfile.city, deliveryProfile.province].filter(Boolean).join(' ') || '-'}
                                        </p>
                                        <p className="mb-1"><strong>{t('COUNTRY')}:</strong> {deliveryProfile.country || '-'}</p>
                                      </Col>
                                    </Row>
                                  )}
                                  {!useAccountDelivery && (
                                    <Fragment>
                                      <Row>
                                        <Col md={6}>
                                          <Form.Group>
                                            <Form.Label>{t('PUBLIC_SHOP_RECIPIENT_NAME')} *</Form.Label>
                                            <Form.Control
                                              value={deliveryForm.name}
                                              onChange={event => updateDeliveryField('name', event.target.value)}
                                            />
                                          </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                          <Form.Group>
                                            <Form.Label>{t('PHONE')} *</Form.Label>
                                            <Form.Control
                                              value={deliveryForm.phone}
                                              onChange={event => updateDeliveryField('phone', event.target.value)}
                                            />
                                          </Form.Group>
                                        </Col>
                                      </Row>
                                      <Form.Group>
                                        <Form.Label>{t('PUBLIC_SHOP_DELIVERY_ADDRESS')} *</Form.Label>
                                        <Form.Control
                                          value={deliveryForm.address}
                                          onChange={event => updateDeliveryField('address', event.target.value)}
                                        />
                                      </Form.Group>
                                      <Row>
                                        <Col md={3}>
                                          <Form.Group>
                                            <Form.Label>{t('PUBLIC_SHOP_CAP')} *</Form.Label>
                                            <Form.Control
                                              value={deliveryForm.cap}
                                              onChange={event => updateDeliveryField('cap', event.target.value)}
                                            />
                                          </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                          <Form.Group>
                                            <Form.Label>{t('CITY')} *</Form.Label>
                                            <Form.Control
                                              value={deliveryForm.city}
                                              onChange={event => updateDeliveryField('city', event.target.value)}
                                            />
                                          </Form.Group>
                                        </Col>
                                        <Col md={2}>
                                          <Form.Group>
                                            <Form.Label>{t('PROVINCE')} *</Form.Label>
                                            <Form.Control
                                              value={deliveryForm.province}
                                              onChange={event => updateDeliveryField('province', event.target.value)}
                                            />
                                          </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                          <Form.Group>
                                            <Form.Label>{t('COUNTRY')} *</Form.Label>
                                            <Form.Control
                                              value={deliveryForm.country}
                                              onChange={event => updateDeliveryField('country', event.target.value)}
                                            />
                                          </Form.Group>
                                        </Col>
                                      </Row>
                                      <Form.Group>
                                        <Form.Label>{t('PUBLIC_SHOP_DELIVERY_NOTES')}</Form.Label>
                                        <Form.Control
                                          as="textarea"
                                          rows={2}
                                          value={deliveryForm.notes}
                                          onChange={event => updateDeliveryField('notes', event.target.value)}
                                          placeholder={t('PUBLIC_SHOP_DELIVERY_NOTES_PLACEHOLDER')}
                                        />
                                      </Form.Group>
                                    </Fragment>
                                  )}
                                  <Form.Check
                                    className="mt-3"
                                    type="checkbox"
                                    id="shop-delivery-confirm"
                                    label={t('PUBLIC_SHOP_DELIVERY_CONFIRM')}
                                    checked={deliveryConfirmed}
                                    disabled={missingDeliveryFields.length > 0}
                                    onChange={event => setDeliveryConfirmed(event.target.checked)}
                                  />
                                </Card.Body>
                              </Card>}
                              <Card className="public-shop-checkout-panel mb-3">
                                <Card.Body>
                                  <Form.Check
                                    type="checkbox"
                                    id="shop-invoice-request"
                                    checked={invoiceRequested}
                                    onChange={event => setInvoiceRequested(event.target.checked)}
                                    label={t('PUBLIC_SHOP_INVOICE_REQUEST')}
                                  />
                                  {invoiceRequested && !hasCompleteInvoiceData(profile) && (
                                    <Alert variant="warning" className="mt-3 mb-0">
                                      {t('PUBLIC_SHOP_INVOICE_REQUIRED')}
                                      {' '}
                                      <Button variant="link" className="p-0 align-baseline" onClick={() => history.push(deliveryProfilePath)}>
                                        {t('PUBLIC_SHOP_EDIT_ACCOUNT_DATA')}
                                      </Button>
                                    </Alert>
                                  )}
                                </Card.Body>
                              </Card>
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <div>{t('PUBLIC_SHOP_ITEMS_TOTAL', {amount: formatEur(cartTotalCents)})}</div>
                                  <div>{deliveryMethod === 'pickup' ? t('PUBLIC_SHOP_PICKUP_TOTAL_FREE') : t('PUBLIC_SHOP_SHIPPING_TOTAL', {amount: shippingCents > 0 ? `EUR ${formatEur(shippingCents)}` : t('PUBLIC_SHOP_FREE')})}</div>
                                  <strong>{t('PUBLIC_SHOP_TOTAL', {amount: formatEur(orderTotalCents)})}</strong>
                                </div>
                                <Button className="public-shop-action public-shop-action--primary" disabled={checkoutLoading || (token && (!canUseWallet || !canCheckoutWithDelivery))} onClick={() => checkout()}>
                                  {checkoutLoading
                                    ? t('PUBLIC_SHOP_CHECKOUT_LOADING')
                                    : !token ? t('PUBLIC_SHOP_LOGIN_TO_PAY') : t('PUBLIC_SHOP_PAY_WITH_WALLET')}
                                </Button>
                              </div>
                            </Fragment>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}

                {loading && (
                  <Row style={{marginTop: 40}}>
                    <Col md={12} className="text-center">
                      <Spinner animation="border" role="status"/>
                    </Col>
                  </Row>
                )}

                {!loading && error && (
                  <Row style={{marginTop: 40}}>
                    <Col md={12}>
                      <Alert variant="danger">{error}</Alert>
                    </Col>
                  </Row>
                )}

                {!loading && !error && publishedCount === 0 && (
                  <Row style={{marginTop: 40}}>
                    <Col md={12}>
                      <Alert variant="secondary">{t('PUBLIC_SHOP_NO_PRODUCTS')}</Alert>
                    </Col>
                  </Row>
                )}

                {!loading && !error && publishedCount > 0 && (
                  <Row className="public-shop-products">
                    {products.map(product => {
                      const selectedVariant = getSelectedVariant(product, selectedVariants[product.id]);
                      const productImages = getProductImages(product, selectedVariant);
                      const requestedImageId = Number(selectedProductImages[product.id]);
                      const image = productImages.find(candidate => candidate.id === requestedImageId) || productImages[0] || null;
                      const imageSrc = getImageSrc(product, image);
                      const primaryImageSrc = getImageSrc(product, null);
                      const specificImageKey = `specific:${product.id}:${image ? image.id : 'primary'}`;
                      const primaryImageKey = `primary:${product.id}`;
                      const specificImageFailed = failedImages[specificImageKey];
                      const primaryImageFailed = failedImages[primaryImageKey];
                      const effectiveImageSrc = specificImageFailed && primaryImageSrc && primaryImageSrc !== imageSrc
                        ? primaryImageSrc
                        : imageSrc;
                      const displayImageSrc = effectiveImageSrc === primaryImageSrc && primaryImageFailed
                        ? null
                        : effectiveImageSrc;
                      const priceCents = getProductPrice(product, selectedVariant);
                      const stockQuantity = selectedVariant ? selectedVariant.stock_quantity : 0;
                      const hasVariants = product.variants && product.variants.length > 0;
                      const quantity = getQuantity(product.id);
                      const canPurchase = selectedVariant && selectedVariant.active && stockQuantity > 0;

                      return (
                        <Col md={6} lg={4} key={product.id} className="public-shop-products__column">
                          <Card className="public-shop-product h-100">
                            {displayImageSrc ? (
                              <Fragment>
                                <img
                                  src={displayImageSrc}
                                  alt={image ? (image.label || selectedVariant?.sku || product.name) : product.name}
                                  onError={() => setFailedImages(current => ({
                                    ...current,
                                    [displayImageSrc === primaryImageSrc ? primaryImageKey : specificImageKey]: true
                                  }))}
                                  className="public-shop-product__image"
                                />
                                {productImages.length > 1 && (
                                  <div className="d-flex flex-wrap px-3 pb-2" aria-label={`Foto ${product.name}`}>
                                    {productImages.map(candidate => {
                                      const candidateSrc = getImageSrc(product, candidate);
                                      const isSelected = image && candidate.id === image.id;
                                      return (
                                        <button
                                          type="button"
                                          key={candidate.id}
                                          className={`btn p-1 mr-2 mb-2 ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                                          onClick={() => setSelectedProductImages(current => ({
                                            ...current,
                                            [product.id]: candidate.id
                                          }))}
                                          aria-label={`Mostra ${candidate.label || `foto ${candidate.id}`}`}
                                        >
                                          <img
                                            src={candidateSrc}
                                            alt={candidate.label || product.name}
                                            style={{width: 58, height: 58, objectFit: 'cover', backgroundColor: '#fff'}}
                                          />
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </Fragment>
                            ) : (
                              <div className="public-shop-product__image-placeholder"><FaShoppingBag aria-hidden="true"/></div>
                            )}
                            <Card.Body className="d-flex flex-column">
                              <div className="public-shop-product__category">
                                <Badge variant={product.status === 'sold_out' ? 'secondary' : 'info'}>
                                  {product.category || t('PUBLIC_SHOP_DEFAULT_CATEGORY')}
                                </Badge>
                              </div>
                              <div className="public-shop-product__identity">
                                <Card.Title>{product.name}</Card.Title>
                                <Card.Text>{product.description || t('PUBLIC_SHOP_NO_DESCRIPTION')}</Card.Text>
                              </div>
                              <div className="public-shop-product__commercial">
                                <div className="public-shop-product__price"><span>{t('PRICE')}</span><strong>EUR {formatEur(priceCents)}</strong></div>
                                {product.sale_price_cents != null && product.sale_price_cents < product.price_cents && (
                                  <div className="public-shop-product__base-price">
                                    {t('PUBLIC_SHOP_BASE_PRICE')} EUR {formatEur(product.price_cents)}
                                  </div>
                                )}
                                <small className={`public-shop-product__stock ${stockQuantity > 0 ? 'is-available' : 'is-sold-out'}`}>
                                  {t('PUBLIC_SHOP_AVAILABILITY')}: {stockQuantity > 0 ? stockQuantity : t('PUBLIC_SHOP_SOLD_OUT')}
                                </small>
                              </div>
                              <div className="public-shop-product__options">
                                {hasVariants && (
                                <Form.Group>
                                  <Form.Label>{t('PUBLIC_SHOP_VARIANT')}</Form.Label>
                                  <Form.Control
                                    as="select"
                                    value={selectedVariants[product.id] || ''}
                                    onChange={event => {
                                      setSelectedVariants(current => ({
                                        ...current,
                                        [product.id]: event.target.value
                                      }));
                                      setSelectedProductImages(current => ({
                                        ...current,
                                        [product.id]: null
                                      }));
                                    }}
                                  >
                                    {product.variants.map(variant => (
                                      <option key={variant.id} value={variant.id}>
                                      {getVariantLabel(variant)}
                                      {' '}
                                        {variant.active ? '' : `(${t('PUBLIC_SHOP_VARIANT_DISABLED')})`}
                                        {' '}
                                        {variant.stock_quantity > 0 ? `- ${variant.stock_quantity} ${t('PUBLIC_SHOP_AVAILABLE')}` : `- ${t('PUBLIC_SHOP_SOLD_OUT')}`}
                                      </option>
                                    ))}
                                  </Form.Control>
                                </Form.Group>
                                )}
                                <Form.Group>
                                  <Form.Label>{t('QUANTITY')}</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min="1"
                                    max={Math.max(stockQuantity, 1)}
                                    disabled={!canPurchase}
                                    value={quantity}
                                    onChange={event => updateQuantity(product.id, event.target.value, stockQuantity)}
                                  />
                                </Form.Group>
                              </div>
                              <div className="public-shop-product__total">
                                <span>{t('PUBLIC_SHOP_SELECTED_TOTAL')}</span><strong>EUR {formatEur(priceCents * quantity)}</strong>
                              </div>
                              <Button
                                className="shop-add-cart-action"
                                disabled={!canPurchase || checkoutLoading}
                                onClick={() => addToCart(product, selectedVariant)}
                              >
                                <span className="shop-add-cart-action__icon" aria-hidden="true"><FaCartPlus/></span>
                                <span>{t('PUBLIC_SHOP_ADD_TO_CART')}</span>
                              </Button>
                              <div className="mt-auto">
                                <small className="text-muted">
                                  SKU: {selectedVariant ? selectedVariant.sku : product.sku}
                                </small>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </div>
            </div>
          </div>
        </Container>
      </Fade>
    </Fragment>
  );
};

export default PublicShop;
